// auth.js - Authentication utilities
import { BASE_URL, HEADERS } from "./config";

/**
 * Validates the current user's token with the backend
 * This ensures the user still exists and credentials are still valid
 * @returns {Promise<{valid: boolean, data: {email: string, role: number} | null, message: string}>}
 */
export const validateToken = async () => {
  const token = localStorage.getItem("authToken");
  const email = localStorage.getItem("userEmail");

  // If no token or email in localStorage, user is not authenticated
  if (!token || !email) {
    return {
      valid: false,
      data: null,
      message: "No authentication token found",
    };
  }

  try {
    const API_URL = BASE_URL + "/api/verify";
    const response = await fetch(API_URL, {
      method: "POST",
      headers: HEADERS,
      body: JSON.stringify({
        token: token.toLowerCase(),
        email: email.toLowerCase(),
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Update stored data in case role or email changed
      if (data.data.email) {
        localStorage.setItem("userEmail", data.data.email);
      }
      if (data.data.role !== undefined) {
        localStorage.setItem("userRole", data.data.role);
      }

      return {
        valid: true,
        data: {
          email: data.data.email,
          role: data.data.role,
        },
        message: "Token is valid",
      };
    } else {
      // Token is invalid - clear localStorage
      clearAuth();
      return {
        valid: false,
        data: null,
        message: data.message || "Token validation failed",
      };
    }
  } catch (error) {
    console.error("Error validating token:", error);
    // On network error, we might want to keep the user logged in
    // Or clear auth depending on your security requirements
    // For strict security, clear auth on any error:
    clearAuth();
    return {
      valid: false,
      data: null,
      message: `Validation error: ${error.message}`,
    };
  }
};

/**
 * Clears all authentication data from localStorage
 */
export const clearAuth = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("userRole");
};

/**
 * Checks if user is authenticated (has token in localStorage)
 * This is a quick check without API validation
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem("authToken");
};

/**
 * Gets the current user's email from localStorage
 * @returns {string | null}
 */
export const getCurrentUserEmail = () => {
  return localStorage.getItem("userEmail");
};

/**
 * Gets the current user's role from localStorage
 * @returns {number | null}
 */
export const getCurrentUserRole = () => {
  const role = localStorage.getItem("userRole");
  return role ? Number(role) : null;
};

